import {Injectable} from '@angular/core';
import {LocalStorageSettings, SETTINGS_STORAGE_KEY} from '../../../nav/local-storage.settings';
import {IMAGE_WARNING_DISMISSED_FLAG} from '../../../nav/dismissible-prefs';

/**
 * MAP-1583: remembers that the user ticked "do not show this again" on the
 * experimental-image-generation warning bubble.
 *
 * User-level UI preference (no per-user backend settings table exists for
 * this), stored in the same `app-settings` blob as the other local settings —
 * the bubble already reads that blob directly for its long-press option. Kept
 * dependency-free on purpose so the sidebar sections that gate the image
 * lookup can inject it without pulling in the whole SettingsService graph.
 */
@Injectable({providedIn: 'root'})
export class ImageWarningPreferenceService {
  isDismissed(): boolean {
    return this.read()[IMAGE_WARNING_DISMISSED_FLAG] === true;
  }

  /**
   * MAP-1583 (review round 3): the checkbox is the single source of truth, so
   * unticking it has to be able to undo a previous dismissal. Clearing removes
   * the flag entirely (same shape a settings reset leaves behind) rather than
   * writing `false`, so the two paths cannot drift.
   */
  setDismissed(dismissed: boolean): void {
    const settings = this.read();
    if (dismissed) {
      settings[IMAGE_WARNING_DISMISSED_FLAG] = true;
    } else {
      delete settings[IMAGE_WARNING_DISMISSED_FLAG];
    }
    this.write(settings);
  }

  private read(): LocalStorageSettings {
    try {
      const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  private write(settings: LocalStorageSettings): void {
    try {
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // Ignore quota/availability errors — the warning simply shows again.
    }
  }
}
